/**
 * Date/time formatting theo ngôn ngữ UI hiện tại — dùng `resolveUILanguage(i18n.language)` (vi/en)
 * làm input, map sang locale chuẩn ICU ('vi-VN'/'en-US') cho Intl.DateTimeFormat/toLocaleDateString.
 * Trước đây hầu hết list page gọi thẳng `new Date(x).toLocaleDateString()` KHÔNG truyền locale —
 * dùng locale mặc định của trình duyệt, không đổi theo ngôn ngữ đang chọn trên UI.
 */

import { formatDate as fmtVnDate, formatDateTime as fmtVnDateTime } from './format';

const localeMap: Record<'en' | 'vi', string> = {
  en: 'en-US',
  vi: 'vi-VN',
};

/** uiLang: kết quả từ resolveUILanguage(i18n.language) — 'en' | 'vi'. */
export function toICULocale(uiLang: 'en' | 'vi'): string {
  return localeMap[uiLang];
}

export function formatLocalizedDate(
  date: Date | string | number,
  uiLang: 'en' | 'vi',
  options?: Intl.DateTimeFormatOptions
): string {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  // EN không nhập nhằng mm/dd: '03 Oct 2026' (tiếng Việt giữ dd/MM/yyyy)
  // Tiếng Việt: luôn dd/MM/yyyy (đệm số 0) theo giờ VN — không để trình duyệt tự chọn "3/10/2026" (task 078)
  if (uiLang === 'vi' && !options) return fmtVnDate(d);
  if (uiLang === 'en' && !options) return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  return d.toLocaleDateString(toICULocale(uiLang), options);
}

export function formatLocalizedDateTime(
  date: Date | string | number,
  uiLang: 'en' | 'vi',
  options?: Intl.DateTimeFormatOptions
): string {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  if (uiLang === 'vi' && !options) return fmtVnDateTime(d);
  if (uiLang === 'en' && !options) return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
  return d.toLocaleString(toICULocale(uiLang), options);
}

export function formatLocalizedTime(
  date: Date | string | number,
  uiLang: 'en' | 'vi',
  options?: Intl.DateTimeFormatOptions
): string {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(toICULocale(uiLang), options);
}
